import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Produit } from 'app/models/produit';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProduitsService } from 'app/services/produits/produits.service';
import { AwscontrolService, UploadResponse, DeleteResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import Swal from "sweetalert2";
import { HttpErrorResponse } from "@angular/common/http";
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, finalize } from 'rxjs/operators';

type ImageUploadMode = 'direct' | 'imageproduit';

@Component({
  selector: 'app-modifier-produit',
  templateUrl: './modifier-produit.component.html',
  styleUrls: ['./modifier-produit.component.scss']
})
export class ModifierProduitComponent implements OnInit {

  produit: Produit = new Produit();

  // Form Controls
  numCommercialFormControl: FormControl;
  emailCommercialFormControl: FormControl;
  agencySearchControl: FormControl;

  // File handling
  selectedFiles: FileList | null = null;
  fileNames: any[] = [];
  allAttch: any[] = [];

  // Images
  public image: string = '';

  // ==================== IMAGE UPLOAD MODE ====================
  imageUploadMode: ImageUploadMode = 'direct';
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;

  // Data
  public idProduitAgency: any;
  agenciesApp: any[] = [];
  filteredAgencies: Observable<any[]>;

  // Selected agency
  NameAgence: string = '';
  selectedAgencyId: string = '';

  // Loading states
  isUploading: boolean = false;
  isSubmitting: boolean = false;
  isLoading: boolean = true;

  // Language support
  agencyAvailableLanguages: string[] = [];
  showEnglishFields: boolean = false;
  showArabicFields: boolean = false;
  languageLabels: { [code: string]: string } = { 'FR': 'Français', 'AR': 'العربية', 'EN': 'English' };

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private produitService: ProduitsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService
  ) {
    this.numCommercialFormControl = new FormControl('');
    this.emailCommercialFormControl = new FormControl('');
    this.agencySearchControl = new FormControl('');
  }

  ngOnInit(): void {
    this.loadAgencies();
    this.idProduitAgency = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadProduct();
  }

  loadAgencies(): void {
    this.agenciesService.getList().subscribe(
      (data: any) => {
        this.agenciesApp = data;
        this.filteredAgencies = this.agencySearchControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filterAgencies(value))
        );
        if (this.produit && this.produit.agencyName) {
          this.setAgencyFromName(String(this.produit.agencyName));
        }
      },
      error => console.error('Error loading agencies:', error)
    );
  }

  loadProduct(): void {
    this.isLoading = true;
    this.produitService.getProduit(this.idProduitAgency).subscribe(
      (data: any) => {
        this.produit = data;
        this.image = data.imageUrl || '';
        if (this.image) this.allAttch[0] = this.image;

        if (data.numCommercial) {
          this.numCommercialFormControl.setValue(data.numCommercial);
        }
        if (data.emailCommercial) {
          this.emailCommercialFormControl.setValue(data.emailCommercial);
        }

        if (this.produit.categoryList) {
          if (typeof this.produit.categoryList === 'string') {
            this.produit.categoryList = (this.produit.categoryList as string).split(',').filter(c => c.trim());
          } else if (!Array.isArray(this.produit.categoryList)) {
            this.produit.categoryList = [];
          }
        } else {
          this.produit.categoryList = [];
        }

        if (data.nomarab) {
          this.produit.nomarab = data.nomarab;
        }
        if (data.nomanglais) {
          this.produit.nomanglais = data.nomanglais;
        }
        if (data.descriptionAnglais) {
          this.produit.descriptionAnglais = data.descriptionAnglais;
        }

        if (data.agencyName) {
          const agencyName = String(data.agencyName);
          this.NameAgence = agencyName;
          if (this.agenciesApp && this.agenciesApp.length > 0) {
            this.setAgencyFromName(agencyName);
          }
        }

        this.isLoading = false;
      },
      error => {
        console.error('Error loading product:', error);
        this.isLoading = false;
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Erreur lors du chargement du produit",
        });
      }
    );
  }

  private setAgencyFromName(name: string): void {
    const found = this.agenciesApp.find(a => a.name === name);
    if (found) {
      this.selectedAgencyId = found.id;
      this.agencySearchControl.setValue(found.name);
      if (found.availableLanguages && Array.isArray(found.availableLanguages)) {
        this.agencyAvailableLanguages = found.availableLanguages.map((l: string) => l.toUpperCase());
        this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');
        this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
      } else {
        this.agencyAvailableLanguages = ['FR'];
        this.showEnglishFields = false;
        this.showArabicFields  = false;
      }
    } else {
      this.agencySearchControl.setValue(name);
    }
  }

  private _filterAgencies(value: string): any[] {
    const filterValue = value ? value.toLowerCase() : '';
    return this.agenciesApp ? this.agenciesApp.filter(agency =>
      agency.name.toLowerCase().includes(filterValue)
    ) : [];
  }

  displayAgency(agency: any): string {
    return agency ? agency.name : '';
  }

  onAgencySelected(event: any): void {
    const selected = event.option.value;
    if (selected) {
      this.NameAgence = selected.name;
      this.selectedAgencyId = selected.id;
      if (selected.availableLanguages && Array.isArray(selected.availableLanguages)) {
        this.agencyAvailableLanguages = selected.availableLanguages.map((l: string) => l.toUpperCase());
        this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');
        this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
      } else {
        this.agencyAvailableLanguages = ['FR'];
        this.showEnglishFields = false;
        this.showArabicFields  = false;
      }
    }
  }

  // ============================================================
  // ==================== IMAGE UPLOAD ==========================
  // ============================================================

  setUploadMode(mode: ImageUploadMode): void {
    if (this.isUploading) return;
    if (this.isAwaitingValidation) {
      this.cancelImagePreview(true);
    }
    this.imageUploadMode = mode;
    console.log('🔄 Mode upload image :', mode);
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'JPEG, PNG, JPG, GIF, WEBP uniquement',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'Max 5MB',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    return true;
  }

  selectFileimg(e: any): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    this.selectedFiles = input.files;

    const reader = new FileReader();
    reader.onload = (ev) => {
      this.previewOriginalUrl = (ev.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.imageUploadMode === 'direct') {
      this.uploadDirect(file, input);
    } else {
      this.uploadViaImageproduit(file, input);
    }
  }

  private uploadDirect(file: File, input: HTMLInputElement): void {
    this.isUploading = true;
    const folder = 'produits';

    this.awsService.uploadFile(file, folder)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe({
        next: (response: UploadResponse) => {
          if (response.success && response.urls && response.urls.length > 0) {
            const url = response.urls[0];
            this.image = url;
            this.allAttch[0] = url;
            localStorage.setItem('prodImg', JSON.stringify(url));
            this.previewOriginalUrl = null;

            Swal.fire({
              icon: "success",
              title: "Image téléchargée",
              text: "Image principale mise à jour",
              timer: 1500,
              showConfirmButton: false
            });
            input.value = '';
          } else {
            Swal.fire({
              icon: "error",
              title: "Erreur",
              text: response.error || "Échec du téléchargement",
            });
            this.previewOriginalUrl = null;
          }
        },
        error: (error) => {
          console.error('Upload error:', error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Erreur lors du téléchargement de l'image",
          });
          this.previewOriginalUrl = null;
        }
      });
  }

  private uploadViaImageproduit(file: File, input: HTMLInputElement): void {
    this.isUploading = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    const folder = 'produits';

    this.awsService.imageproduit([file], folder)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe({
        next: (response: ImageUploadResponse) => {
          if (response.success && response.urls && response.urls.length > 0) {
            this.previewProcessedUrl = response.urls[0];
            this.isAwaitingValidation = true;
            input.value = '';
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: (response.errors && response.errors[0]) || "Échec du traitement de l'image",
            });
            this.previewOriginalUrl = null;
          }
        },
        error: () => {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: "Impossible de traiter l'image",
          });
          this.previewOriginalUrl = null;
        }
      });
  }

  validateImagePreview(): void {
    if (!this.previewProcessedUrl) return;

    this.image = this.previewProcessedUrl;
    this.allAttch[0] = this.previewProcessedUrl;
    localStorage.setItem('prodImg', JSON.stringify(this.previewProcessedUrl));

    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    this.selectedFiles = null;

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image a été acceptée pour ce produit.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelImagePreview(silent: boolean = false): void {
    const urlToDelete = this.previewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFiles = null;
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: "L'image traitée sera supprimée et vous pourrez en choisir une autre.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  deleteMainImage(): void {
    if (this.isAwaitingValidation) {
      this.cancelImagePreview();
      return;
    }
    if (!this.image) return;

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Cette action est irréversible!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(this.image).subscribe(
          (response: DeleteResponse) => {
            if (response.status === 'success') {
              this.image = '';
              this.allAttch[0] = '';
              Swal.fire({
                icon: 'success',
                title: 'Supprimé!',
                text: "L'image a été supprimée.",
                timer: 1500
              });
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: response.error || "Échec de la suppression",
              });
            }
          },
          (error) => {
            console.error('Delete error:', error);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: "Erreur lors de la suppression",
            });
          }
        );
      }
    });
  }

  // ========== SUBMIT ==========
  updateProduit(): void {
    if (this.isAwaitingValidation) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation requise',
        text: "Veuillez valider ou refuser l'image avant de continuer.",
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    if (this.isUploading) {
      Swal.fire({
        icon: "warning",
        title: "Téléchargement en cours",
        text: "Veuillez attendre la fin du téléchargement de l'image",
      });
      return;
    }

    this.isSubmitting = true;

    let categoryString = '';
    if (this.produit.categoryList && Array.isArray(this.produit.categoryList)) {
      categoryString = this.produit.categoryList.filter(c => c.trim()).join(',');
    } else if (typeof this.produit.categoryList === 'string') {
      categoryString = this.produit.categoryList;
    }

    const payload: any = {
      id: this.produit.id,
      name: this.produit.name || '',
      nomarab: this.produit.nomarab || '',
      nomanglais: this.produit.nomanglais || '',
      description: this.produit.description || '',
      descriptionAnglais: this.produit.descriptionAnglais || '',
      prix: this.produit.prix || '',
      agencyName: this.NameAgence || '',
      categoryList: categoryString,
      imageUrl: this.image || this.produit.imageUrl || '',
      videoUrlProduit: this.produit.videoUrlProduit || '',
      nomCommercial: this.produit.nomCommercial || '',
      numCommercial: this.numCommercialFormControl.value || '',
      emailCommercial: this.emailCommercialFormControl.value || ''
    };

    console.log('📦 Updating with payload:', payload);

    this.produitService.updateProduit(this.idProduitAgency, payload).subscribe(
      () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Produit modifié avec succès",
          timer: 2000
        });
        setTimeout(() => {
          if (this.selectedAgencyId) {
            this.router.navigate(['/list-produits', this.selectedAgencyId]);
          } else {
            this.navigateBack();
          }
        }, 500);
      },
      (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        console.error("Update error:", error);
        let errorMsg = "Une erreur s'est produite lors de la modification du produit";
        if (error.error?.message) errorMsg = error.error.message;
        Swal.fire({ icon: "error", title: "Erreur", text: errorMsg });
      }
    );
  }

  navigateBack(): void {
    this.location.back();
  }
}